import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {
  AddChipTime,
  CompetitorSaveControlPointResult,
  CompetitorSaveControlPointResultStatus,
  CompetitorStatus,
  EventItem,
  Lookups,
  MeasurementTimeItemHolder,
  Race,
  RaceSettings,
  ResultProcessingType,
  TOAST_STATE,
  Waypoint,
  WaypointType,
  Competitor, MultiLangField
} from '@model';
import {TranslocoService} from '@ngneat/transloco';
import {getErrorMessage, getLanguageValue, localizedDateString} from '@utility';
import {debounceTime, distinctUntilChanged, finalize} from 'rxjs';
import {
  CompetitorService,
  LoaderService,
  MeasurementsService,
  MeasurementTimeItemHolderService,
  ToastService
} from '@services';
import {AbstractComponent, AddChipTimeModalComponent, ConfirmationModalComponent} from "@components";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {v4 as uuidv4} from 'uuid';

@Component({
  selector: 'app-event-measurements-points-laps',
  templateUrl: './event-measurements-points-laps.component.html',
  styleUrl: './event-measurements-points-laps.component.scss',
  standalone: false
})
export class EventMeasurementsPointsLapsComponent extends AbstractComponent implements OnInit{
  @Input() measurementsPointsView = true;
  @Input() eventItem?: EventItem;
  @Input() waypointsList: Waypoint[] = [];
  @Input() allWaypointList: Waypoint[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Output() refreshCounters = new EventEmitter<void>();

  waypoints: Waypoint[] = [];
  waypointIds: number[] = [];
  waypointLabels: string[] = [];
  raceListIds: number[] = [];
  raceListLabels: string[] = [];
  raceList: Race[] = [];
  measurementTimeItemHolders: MeasurementTimeItemHolder[] = [];
  competitorControlPointTimeItemsErrors: string[] = [];
  isLoading: boolean = false;
  showLapDistancePassedColumn = false;
  showStatusColumn = false;
  showStartNumberExtensionColumn = false;
  showTeamMemberColumn = false;
  raceSettingsMap: Map<number, RaceSettings> = new Map<number, RaceSettings>();

  /** Plain field (not a getter) backing the loading-row colspan — recomputed via
   *  recomputeVisibleColumnCount() whenever a column-visibility flag changes, so the template
   *  never needs to call a getter/method during change detection. */
  visibleColumnCount = 4;

  /** Plain field (not a method/getter call) backing the save-to-server button's disabled state
   *  — recomputed via recomputeSaveToServerDisabled() whenever the row list, a row's competitor,
   *  a row's hasStartTime, or the form's validity changes, so the template never calls back into
   *  the component during change detection. */
  saveToServerDisabled = true;

  selectedRace: Race | undefined;

  inputForm = this.formBuilder.group({
    competitorControlPointTimeItems: this.formBuilder.array([]),
    controlPointType: new FormControl(),
    raceId: new FormControl(-1)
  });

  raceNamesMap: Map<number, MultiLangField> = new Map();


  constructor(
      public formBuilder: FormBuilder,
      private translocoService: TranslocoService,
      private competitorService: CompetitorService,
      private measurementService: MeasurementsService,
      private loaderService: LoaderService,
      private toastService: ToastService,
      private modalService: NgbModal,
      private measurementTimeItemHolderService: MeasurementTimeItemHolderService,
  ) {
    super();

  }

  ngOnInit(): void {
    this.filterRaceList()
    this.initWaypoints();
    this.initRaceFormControls();
    this.getDataFromLocalStorage(true);
    this.inputForm.controls.competitorControlPointTimeItems.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
    ).subscribe(this.updateMeasurementTimeItemHolders);
    this.subscriptions.push(this.inputForm.controls.raceId.valueChanges.subscribe(val => {
      if (val && val > -1) {
        this.selectedRace = this.raceList.find(r => r.id === val);
      }
      else {
        this.selectedRace = undefined;
      }
    }));
  }


  private getDataFromLocalStorage = (update?: boolean) => {
    this.isLoading = !!update;
    this.measurementTimeItemHolderService.getHolderForEvent(this.measurementsPointsView, this.eventItem!.id!, update)
        .pipe(
            finalize(() => {
              this.isLoading = false;
            })
        )
        .subscribe(holder => {
          const measurementTimeItemHolders: MeasurementTimeItemHolder[] = holder;
          if (measurementTimeItemHolders?.length) {
            this.measurementTimeItemHolders = measurementTimeItemHolders;
            this.competitorControlPointTimeItems.clear();
            this.measurementTimeItemHolders.forEach(item => this.addMeasurementTimeItem(item, false));
          }
          if (!this.measurementsPointsView) {
            const startNumbers = Array.from(
                new Set(measurementTimeItemHolders
                    .filter(holder => holder.startNumber)
                    .map(holder => holder.startNumber))
            );
            startNumbers.forEach(startNumber => this.saveData(startNumber))
          }
        })
  }

  private filterRaceList = () => {
    this.raceList = (this.eventItem?.racesList ?? []).filter(item =>
        this.measurementsPointsView ? !(RaceSettings.isRoundRace(item.raceSettings?.resultProcessingType) || Race.isBackyardUltra(item.sport)) :
            (RaceSettings.isRoundRace(item.raceSettings?.resultProcessingType) || Race.isBackyardUltra(item.sport))
    );
    this.raceList.forEach(r => {
      if (r.id && r.raceSettings) {
        this.raceSettingsMap.set(r.id!, r.raceSettings);
      }
    })
  }

  private initWaypointFormControls = () => {
    this.waypointIds = this.waypoints.map(item => item.id!);
    this.waypointLabels = this.waypoints.map(item => {
      const typeTranslation = this.translocoService.translate(`organiser.event.details.control.point.waypointType.${item.waypointTypeId}`);
      return item.waypointTypeId === WaypointType.CONTROL_WAYPOINT ? `${getLanguageValue(item.pointNameHr, item.pointNameEn, item.pointNameIt, item.pointNameSlo, item.pointNameSr, item.pointNameBs, this.translocoService.getActiveLang()) ?? '-'}` : typeTranslation;
    });
    this.waypointIds.length && this.inputForm.controls.controlPointType.setValue(this.waypointIds[this.waypointIds.length - 1]);
  }

  private initRaceFormControls = () => {
    this.raceListIds = [this.ALL];
    this.raceListIds.push(...this.raceList.map(item => item.id!));
    this.raceListLabels = [this.translocoService.translate("measurements.measurement.races.all")];
    this.raceListLabels.push(...this.raceList.map(item => getLanguageValue(item.name, item.nameEng, item.nameIta, item.nameSlo,item.nameSr,item.nameBs, this.translocoService.getActiveLang()) ?? ''));
    this.raceNamesMap = new Map();
    this.raceNamesMap = new Map(
        this.raceList
            .filter(race => race.id != null)
            .map(race => [race.id!, {
              hr: race.name,
              en: race.nameEng,
              it: race.nameIta,
              slo: race.nameSlo,
              sr: race.nameSr,
              bs: race.nameBs,
            } as MultiLangField])
    );
  }

  get competitorControlPointTimeItems(): FormArray<FormGroup> {
    return this.inputForm.controls.competitorControlPointTimeItems as FormArray;
  }

  private initWaypoints = () => {
    this.waypoints = [...this.waypointsList];
    this.initWaypointFormControls();
  }

  public addMeasurementTimeItem = (item?: MeasurementTimeItemHolder, fromUi: boolean = true) => {
    const formGroup = this.formBuilder.group({
      time: new FormControl(item?.time ?? new Date()),
      penaltyMinutes: new FormControl(item?.penaltyMinutes ?? 0),
      lapDistancePassed: new FormControl(item?.lapDistancePassed),
      status: new FormControl(item?.status ?? (this.measurementsPointsView ? CompetitorStatus.FIN: undefined)),
      checkpoints: new FormControl(item?.checkpoints),
      startNumber: new FormControl(item?.startNumber),
      waypointId: new FormControl(item?.waypointId ?? (this.measurementsPointsView && this.waypointIds.length ? this.inputForm.controls.controlPointType.value : null)),
      raceId: new FormControl(item?.competitor?.raceId ?? (item?.raceId ?? this.inputForm.controls.raceId.value)),
      teamMemberId: new FormControl(item?.teamMemberId)
    });
    if (fromUi) {
      this.measurementTimeItemHolders.push(this.createMeasurementTimeItemHolder(formGroup));
    }
    this.handleLapPassedDistanceControl(formGroup, item?.competitor?.id ? formGroup.get("raceId")?.value ?? undefined : undefined);
    this.competitorControlPointTimeItems.push(formGroup);
    this.competitorControlPointTimeItemsErrors.push(this.getErrors(formGroup));
    let index = this.competitorControlPointTimeItems.controls.length - 1;
    this.handleMaxLapCountControl(formGroup, item?.competitor?.id ? formGroup.get("raceId")?.value ?? undefined : undefined, index);
    if (this.measurementTimeItemHolders[index]) {
      this.measurementTimeItemHolders[index].isConcurrentRow = false;
      this.measurementTimeItemHolders[index].teamMembers = [];
      this.measurementTimeItemHolders[index].teamMemberIds = [];
      this.measurementTimeItemHolders[index].teamMemberLabels = [];
      this.measurementTimeItemHolders[index].hasStartTime = false;
    }
    this.handleTeamMemberControl(formGroup, index, item?.competitor);
    this.competitorControlPointTimeItems.at(index).valueChanges.subscribe(_value => {
      this.competitorControlPointTimeItemsErrors[index] = this.getErrors(this.competitorControlPointTimeItems.at(index))
    })
    formGroup.get('teamMemberId')?.valueChanges.subscribe(_value => {
      this.updateRowStartTime(index, this.measurementTimeItemHolders[index]?.competitor);
    })
    this.inputForm.updateValueAndValidity();
    this.recomputeSaveToServerDisabled();
  }

  private createMeasurementTimeItemHolder = (formGroup: FormGroup): MeasurementTimeItemHolder => ({
    time: formGroup.get('time')?.value,
    penaltyMinutes: formGroup.get('penaltyMinutes')?.value,
    lapDistancePassed: formGroup.get('lapDistancePassed')?.value,
    status: formGroup.get('status')?.value,
    startNumber: formGroup.get('startNumber')?.value,
    waypointId: formGroup.get('waypointId')?.value,
    raceId: formGroup.get('raceId')?.value,
    checkpoints: formGroup.get('checkpoints')?.value,
    teamMemberId: formGroup.get('teamMemberId')?.value,
    key: uuidv4(),
    saveResult: CompetitorSaveControlPointResultStatus.OK,
    isConcurrentRow: false,
    teamMemberIds: [],
    teamMemberLabels: [],
    hasStartTime: false
  });

  public onDeleteMeasurementItem = (index: number) => {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("measurements.controlpoints.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("measurements.controlpoints.delete.dialog.text");

    modalRef.result.then((_) => {
      this.deleteMeasurementItem(index);
    }, (_) => {
      /// ignore
    });
  }

  public onDeleteAllMeasurementItem = () => {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("measurements.controlpoints.delete.all.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("measurements.controlpoints.delete.all.dialog.text");

    modalRef.result.then((_) => {
      this.measurementTimeItemHolders.forEach(holder => holder.subscription?.unsubscribe());
      this.measurementTimeItemHolders = [];
      this.competitorControlPointTimeItems.clear();
      this.saveData();
      this.inputForm.updateValueAndValidity();
      this.recomputeSaveToServerDisabled();
    }, (_) => {
      /// ignore
    });
  }

  public deleteMeasurementItem = (index: number) => {
    this.measurementTimeItemHolders[index].subscription?.unsubscribe();
    if (!this.measurementsPointsView) {
      const {startNumber} = this.measurementTimeItemHolders[index];
      this.measurementTimeItemHolders.splice(index, 1);
      if (startNumber) {
        this.saveData(startNumber);
      }
    } else {
      this.measurementTimeItemHolders.splice(index, 1);
    }
    this.competitorControlPointTimeItems.removeAt(index);
    if (index == 0) {
      this.saveData();
    }
    this.inputForm.updateValueAndValidity();
    this.recomputeSaveToServerDisabled();
  }


  private updateMeasurementTimeItemHolders = (value: any) => {
    value.forEach((item:any, index:number) => {
      let assignLaps : boolean = false;
      if (item.startNumber && (this.measurementTimeItemHolders[index].startNumber !== item.startNumber || this.measurementTimeItemHolders[index].raceId !== item.raceId || this.measurementTimeItemHolders[index].waypointId !== item.waypointId)) {
        if (!this.measurementsPointsView && this.measurementTimeItemHolders[index].startNumber !== item.startNumber) {
          //update other laps if start number exists
          const changedStartNumber = this.measurementTimeItemHolders[index].startNumber;
          if (changedStartNumber) {
            const firstIndex = this.measurementTimeItemHolders.findIndex(
                (item, i) => i !== index && item.startNumber === changedStartNumber
            );
            if (firstIndex !== -1) {
              this.onRefreshMeasurementItem(firstIndex, true)
            }
          }
        }
        this.measurementTimeItemHolders[index].subscription = this.competitorService.fetchCompetitor(item.startNumber, item.raceId !== -1 ? item.raceId : undefined, this.eventItem?.id, item.waypointId, !this.measurementsPointsView).subscribe(competitor => {
          this.measurementTimeItemHolders[index].competitor = competitor;
          if (competitor?.id) { /// competitor is unrecognized
            this.competitorControlPointTimeItems.at(index).get('raceId')?.setValue(competitor!.raceId, {onlySelf: true, emitEvent: false});
            this.measurementTimeItemHolders[index].raceId = competitor?.raceId;
          }
          if (this.measurementsPointsView) {
            this.saveData();
          } else {
            if (competitor?.id && item.raceId != -1 && item.raceId) {
              this.saveData(item.startNumber);
            }
            else {
              this.measurementTimeItemHolders[index].lap = undefined;
              this.saveData()
            }
          }
          this.handleLapPassedDistanceControl(this.competitorControlPointTimeItems.at(index), competitor?.id ? competitor?.raceId : undefined);
          this.handleMaxLapCountControl(this.competitorControlPointTimeItems.at(index), competitor?.id ? competitor?.raceId : undefined, index);
          this.handleTeamMemberControl(this.competitorControlPointTimeItems.at(index) as FormGroup, index, competitor);
          this.competitorControlPointTimeItems.at(index).updateValueAndValidity();
        });
      }
      //time changed
      else if (item.startNumber && item.time !== this.measurementTimeItemHolders[index].time && this.measurementTimeItemHolders[index].competitor && !this.measurementsPointsView) {
        assignLaps = true;
      }

      if (!item.startNumber) {
        this.measurementTimeItemHolders[index].competitor = undefined;
        this.measurementTimeItemHolders[index].lap = undefined;
        this.handleLapPassedDistanceControl(this.competitorControlPointTimeItems.at(index), undefined);
        this.handleMaxLapCountControl(this.competitorControlPointTimeItems.at(index), undefined, index);
        this.handleTeamMemberControl(this.competitorControlPointTimeItems.at(index) as FormGroup, index, undefined);
      }
      if (!this.equalUIValues(this.measurementTimeItemHolders[index], item)) {
        item.saveResult = CompetitorSaveControlPointResultStatus.OK;
        if (!this.measurementsPointsView) {
          assignLaps = true;
        }
      }
      this.measurementTimeItemHolders[index] = { ...this.measurementTimeItemHolders[index], ...item };
      assignLaps ? this.saveData(item.startNumber): this.saveData();
    });
  }

  equalUIValues(a: MeasurementTimeItemHolder, b: MeasurementTimeItemHolder) {
    return a.time === b.time && a.startNumber === b.startNumber && a.raceId === b.raceId &&
        ((!a.lapDistancePassed && !b.lapDistancePassed ) ||
            a.lapDistancePassed === b.lapDistancePassed)
        && a.checkpoints === b.checkpoints
        && (!a.teamMemberId || !b.teamMemberId || a.teamMemberId === b.teamMemberId)        && a.penaltyMinutes === b.penaltyMinutes &&
        ((!a.status && !b.status) || a.status === b.status)
        && a.waypointId === b.waypointId;
  }

  getErrors(formGroup: any): string {
    if (formGroup instanceof FormGroup) {
      for (const controlName of Object.keys(formGroup.controls)) {
        const control = formGroup.get(controlName) as FormControl;
        if (control?.errors) {
          const errorMessage = getErrorMessage(control.errors, this.translocoService, controlName)?.at(0);
          if (errorMessage) {
            return errorMessage;
          }
        }
      }
    }
    return "";
  }

  onRefreshMeasurementItem(index:number, hideLoading?: boolean) {
    if (this.measurementTimeItemHolders[index].startNumber) {
      if (!hideLoading) {
        this.loaderService.startLoading();
      }
      this.measurementTimeItemHolders[index].subscription = this.competitorService.fetchCompetitor(this.measurementTimeItemHolders[index].startNumber!, this.measurementTimeItemHolders[index].raceId, this.eventItem?.id, this.measurementTimeItemHolders[index].waypointId).pipe(finalize(() => {
        this.loaderService.dismissLoading();
      })).subscribe(competitor => {
        if (!competitor) {
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("measurements.unrecognized.competitor"));
        }
        this.measurementTimeItemHolders[index].competitor = competitor;
        if (competitor?.id) {
          this.competitorControlPointTimeItems.at(index).get('raceId')?.setValue(competitor!.raceId);
        }
        if (this.measurementsPointsView) {
          this.saveData();
        } else if (competitor?.id) {
          this.saveData(this.measurementTimeItemHolders[index].startNumber!);
        } else {
          if (!hideLoading) {
            this.loaderService.dismissLoading();
          }
          this.saveData();
        }
        this.handleLapPassedDistanceControl(this.competitorControlPointTimeItems.at(index), competitor?.id ? competitor?.raceId : undefined);
        this.handleMaxLapCountControl(this.competitorControlPointTimeItems.at(index), competitor?.id ? competitor?.raceId : undefined, index);
        this.handleTeamMemberControl(this.competitorControlPointTimeItems.at(index) as FormGroup, index, competitor);
      });
    }
  }

  onRefreshAllMeasurementItem() {
    for (let i: number = 0; i < this.measurementTimeItemHolders.length; i++) {
      this.onRefreshMeasurementItem(i, true);
    }
  }


  assignLapsToCompetitors(startNumber: string) {
    const holders = this.measurementTimeItemHolders;

    const indexedItems = holders
        .map((item, index) => ({
          index,
          time: item.time!,
          startNumber: item.startNumber,
          competitor: item.competitor
        }))
        .filter(i => i.time && i.startNumber === startNumber && i.competitor);

    if (!indexedItems.length) return;

    const competitor = indexedItems[0].competitor!;
    const raceSettings: RaceSettings | undefined = competitor.raceId ? this.raceSettingsMap.get(competitor.raceId!) : undefined;

    const minimumLapDuration = Number(raceSettings?.minimumLapDuration) || undefined;
    let maxLapsCount: number | undefined;

    if (raceSettings?.resultProcessingType === ResultProcessingType.ROUND_RACE && raceSettings.maxLapsCount) {
      maxLapsCount = Number(raceSettings?.maxLapsCount) || undefined;
    }

    const savedItems = (competitor.laps ?? [])
        .filter(l => l.time)
        .map(l => ({ index: -1, time: l.time! }));

    const allItems = [...savedItems, ...indexedItems].sort(
        (a, b) => a.time.getTime() - b.time.getTime()
    );

    let previousValidTime = competitor.startTime;
    let lap = 0;

    allItems.forEach((item, i) => {
      if (item.index === -1) {
        previousValidTime = item.time;
        lap++;
        return;
      }

      const holder = holders[item.index];
      const holderTime = holder.time!;
      const previousTime = i === 0 ? competitor.startTime : allItems[i - 1].time;

      const isDuplicate = holderTime.getTime() === previousTime?.getTime();
      const timeDiff =
          previousValidTime ? (holderTime.getTime() - previousValidTime.getTime()) / 1000 : undefined;

      let saveResult: CompetitorSaveControlPointResultStatus =
          (holder.saveResult !== CompetitorSaveControlPointResultStatus.MINIMAL_LAP_DURATION_ERROR
              ? holder.saveResult
              : CompetitorSaveControlPointResultStatus.OK) ?? CompetitorSaveControlPointResultStatus.OK;
      if (isDuplicate) {
        saveResult = CompetitorSaveControlPointResultStatus.DUPLICATE_LAP_ENTRY;
      }

      const minimalLapError =
          timeDiff && previousValidTime && minimumLapDuration && timeDiff < minimumLapDuration;

      if (minimalLapError) {
        saveResult = CompetitorSaveControlPointResultStatus.MINIMAL_LAP_DURATION_ERROR;
        holder.errorMessage = this.getMinimalLapErrorMessage(minimumLapDuration!, previousTime!, i === 0);
      }
      else {
        holder.errorMessage = undefined;
      }

      if (maxLapsCount && lap + 1 > maxLapsCount) {
        saveResult = CompetitorSaveControlPointResultStatus.MAX_LAPS_COUNT_REACHED;
      }
      if (saveResult === CompetitorSaveControlPointResultStatus.OK) {
        lap++;
        previousValidTime = holderTime;
      }

      holder.saveResult = saveResult;
      holder.lap = saveResult === CompetitorSaveControlPointResultStatus.OK ? lap : undefined;

      this.competitorControlPointTimeItems
          .at(item.index)
          .get('lap')
          ?.setValue(holder.lap, { onlySelf: true, emitEvent: false });

      if (maxLapsCount) {
        this.handleMaxLapCountControl(
            this.competitorControlPointTimeItems.at(item.index),
            competitor.raceId,
            item.index
        );
      }
    });
  }


  getMinimalLapErrorMessage(minimumLapDuration: number, time: Date | undefined, isStart: boolean = false): string {
    const errorMessage1 = this.translocoService.translate('measurements.competitor.control.point.save.error.minimal.lap.error.1', {
      seconds: minimumLapDuration,
    });

    const errorMessageLap =
        this.translocoService.translate(!isStart ? 'measurements.competitor.control.point.save.error.minimal.lap.error.lap' :
            'measurements.competitor.control.point.save.error.minimal.lap.error.start');

    const errorMessage2 = this.translocoService.translate('measurements.competitor.control.point.save.error.minimal.lap.error.2', {
      time: localizedDateString(time, 'dd.MM.yyyy HH:mm:ss.SSS', this.translocoService.getActiveLang()),
    });

    return `${errorMessage1} ${errorMessageLap} ${errorMessage2}`;
  }

  saveData(startNumber?:string) {
    if (startNumber) {
      this.assignLapsToCompetitors(startNumber)
    }
    if (this.eventItem?.id) {
      this.measurementTimeItemHolderService.saveHolderForEvent(this.measurementsPointsView, this.eventItem!.id!, JSON.stringify(this.measurementTimeItemHolders.map(item => ({...item, subscription: undefined}))))
    }
  }

  private recomputeSaveToServerDisabled(): void {
    this.saveToServerDisabled = this.measurementTimeItemHolders.length === 0
        || this.measurementTimeItemHolders.every((item: MeasurementTimeItemHolder) => !item.startNumber || !item.competitor || !item.hasStartTime)
        || this.competitorControlPointTimeItems.invalid;
  }

  saveToServer = () => {
    let competitorsToSave = this.measurementTimeItemHolders.filter((item: MeasurementTimeItemHolder) => item.startNumber && item.competitor && item.hasStartTime && item.saveResult === CompetitorSaveControlPointResultStatus.OK).map((item: MeasurementTimeItemHolder) => {
      let competitor: Competitor = new Competitor();
      competitor.id = item.competitor!.id;
      competitor.raceId = item.competitor!.raceId;
      competitor.startNumber = item.competitor!.startNumber;
      const controlPointTime = {
        time: item.time,
        penaltyMinutes: item.penaltyMinutes,
        lapDistancePassed: item.lapDistancePassed,
        checkpoints: item.checkpoints,
        controlWaypoint: this.waypoints.find(waypoint => waypoint.id === item.waypointId),
        lap: item.lap,
        key: item.key,
        competitorStatus: item.status,
      };

      if (item.isConcurrentRow && item.teamMemberId != null) {
        // Concurrent team race: the measurement belongs to the selected member, not the team competitor as a whole
        const selectedMember = item.teamMembers?.find(m => m.id === item.teamMemberId);
        competitor.teamMembers = selectedMember
            ? [{ ...selectedMember, controlPointsTimes: [controlPointTime] }]
            : [];
        competitor.controlPointsTimes = undefined;
      }
      else {
        competitor.controlPointsTimes = [controlPointTime];
      }

      competitor.status = item.status;
      return competitor;
    });
    let competitorsToLeave = this.measurementTimeItemHolders.filter((item: MeasurementTimeItemHolder ) => !(item.startNumber && item.competitor && item.hasStartTime && item.saveResult === CompetitorSaveControlPointResultStatus.OK));
    if (competitorsToSave.length === 0) {
      this.toastService.showToast(TOAST_STATE.warning, this.translocoService.translate("measurements.controlpoints.savetoserver.warning"));
      return;
    }

    const handleSaveSuccess = (competitorResults: CompetitorSaveControlPointResult[]) => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.success" : "measurements.laps.savetoserver.success"));

      const items: MeasurementTimeItemHolder[] = []
      competitorResults.forEach(competitorResult => {
        const holder = this.measurementTimeItemHolders.find(item => item.key === competitorResult.key);
        if (holder) {
          // Only patch the single member the backend echoed back into the holder's own kept
          // roster (holder.teamMembers) — never replace the whole roster with the response's
          // single-member array, or every other member on the team gets lost from the holder.
          const updatedMember = competitorResult.competitor?.teamMembers?.[0];
          if (updatedMember && holder.teamMembers?.length) {
            holder.teamMembers = holder.teamMembers.map(m => m.id === updatedMember.id ? { ...m, ...updatedMember } : m);
          }

          holder.competitor = holder.competitor
              ? { ...holder.competitor, status: competitorResult.competitor?.status ?? holder.competitor.status }
              : competitorResult.competitor;
          holder.saveResult = competitorResult.status;
          if (competitorResult.status !== CompetitorSaveControlPointResultStatus.OK) {
            items.push(holder)
          }
        }
      });

      this.measurementTimeItemHolders = [...items, ...competitorsToLeave];
      if (this.measurementTimeItemHolders.length === 0) {
        this.measurementTimeItemHolders = [];
        this.competitorControlPointTimeItems.clear();
        this.measurementTimeItemHolderService.deleteHolderForEvent(this.measurementsPointsView, this.eventItem!.id!)
      } else {
        this.measurementTimeItemHolderService.saveHolderForEvent(this.measurementsPointsView, this.eventItem!.id!,
            JSON.stringify(this.measurementTimeItemHolders.map(item => ({...item, subscription: undefined}))))
        this.measurementTimeItemHolders = [];
        this.competitorControlPointTimeItems.clear();
        this.getDataFromLocalStorage();
      }

      this.refreshCounters.emit();
      this.inputForm.updateValueAndValidity();
      this.recomputeSaveToServerDisabled();
    };

    const handleSaveError = () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.error" : "measurements.laps.savetoserver.error"));
    };
    this.loaderService.startLoading();
    this.measurementsPointsView
        ? this.measurementService.saveCompetitorsControlPoints(competitorsToSave, this.eventItem!.id!, handleSaveSuccess, handleSaveError)
        : this.measurementService.saveOrUpdateCompetitorsLaps(competitorsToSave, this.eventItem!.id!, handleSaveSuccess, handleSaveError);
  }

  getWaypointType(waypointId: number): WaypointType | undefined {
    return this.waypoints.find(waypoint => waypoint.id === waypointId)?.waypointTypeId;
  }

  addChipTime() {
    const waypointsList = this.measurementsPointsView ? this.allWaypointList : this.allWaypointList.filter(item => item.waypointTypeId === WaypointType.START_WAYPOINT);
    let waypointIds = waypointsList.map(item => item.id!);
    let waypointLabels = waypointsList.map(item => {
      const typeTranslation = this.translocoService.translate(`organiser.event.details.control.point.waypointType.${item.waypointTypeId}`);
      return item.waypointTypeId === WaypointType.CONTROL_WAYPOINT ? `${typeTranslation} ${getLanguageValue(item.pointNameHr, item.pointNameEn, item.pointNameIt, item.pointNameSlo, item.pointNameSr, item.pointNameBs, this.translocoService.getActiveLang()) ?? '-'}` : typeTranslation;
    });

    if (this.measurementsPointsView === false) {
      waypointIds = [-1, ...waypointIds];
      waypointLabels = [this.translocoService.translate("measurements.controlpoints.lap"), ...waypointLabels];
    }

    const modalRef = this.modalService.open(AddChipTimeModalComponent, {
      centered: true,
      windowClass: 'chip-time-modal'
    });
    modalRef.componentInstance.title = this.translocoService.translate("measurements.controlpoints.button.add.chip.time");
    modalRef.componentInstance.waypointIds = waypointIds;
    modalRef.componentInstance.waypointLabels = waypointLabels;
    modalRef.componentInstance.raceListIds = this.raceListIds;
    modalRef.componentInstance.raceListLabels = this.raceListLabels;
    modalRef.componentInstance.measurementsPointsView = this.measurementsPointsView;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.event = this.eventItem;
    this.subscriptions.push(modalRef.componentInstance.onSave.subscribe((addChipTime: AddChipTime) => {
      this.onAddChipTime(addChipTime);
    }))
  }

  onAddChipTime(addChipTime: AddChipTime): void {
    this.loaderService.startLoading();
    addChipTime.eventId = this.eventItem!.id!;
    if (addChipTime.raceId === this.ALL) {
      addChipTime.raceId = undefined;
    }
    this.measurementService.addChipTime(addChipTime, !this.measurementsPointsView, (competitorSaveControlPointResults: CompetitorSaveControlPointResult[])=>{
      this.loaderService.dismissLoading();
      if (competitorSaveControlPointResults.every(result => result.status === CompetitorSaveControlPointResultStatus.OK)) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.success" : "measurements.laps.savetoserver.success"));
      } else {
        let toastState = TOAST_STATE.success;
        let resultMessage = "";
        if (competitorSaveControlPointResults.every(result => result.status !== CompetitorSaveControlPointResultStatus.OK)) {
          toastState = TOAST_STATE.danger;
          resultMessage = this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.error" : "measurements.laps.savetoserver.error") + "</br>";
        } else {
          resultMessage = this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.savetoserver.success" : "measurements.laps.savetoserver.success") + "</br>";
        }
        Object.values(CompetitorSaveControlPointResultStatus).forEach(value => {
          if (value !== CompetitorSaveControlPointResultStatus.OK) {
            const chipNumbers = this.findChipNumbersByStatus(competitorSaveControlPointResults, value);
            if ((chipNumbers.length ?? 0) > 0) {
              const uniqueChipNumbers = [...new Set(chipNumbers)];
              resultMessage += "</br>" + this.translocoService.translate(`measurements.competitor.control.point.save.error.${value}`) + " : " + "(" + uniqueChipNumbers.join(', ') + ")";
            }
          }
        });
        this.toastService.showToast(toastState, resultMessage);
      }
      this.refreshCounters.emit();
    }, ()=>{
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(this.measurementsPointsView ? "measurements.controlpoints.chip.savetoserver.error" : "measurements.laps.chip.savetoserver.error"));
    });
  }

  handleLapPassedDistanceControl(formGroup: FormGroup, raceId: number | undefined) {
    const raceSettings: RaceSettings | undefined = raceId ? this.raceSettingsMap.get(raceId!) : undefined;
    if (raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED && raceSettings.lapLength && formGroup.get('startNumber')?.value) {
      formGroup.get('lapDistancePassed')?.enable({ emitEvent: false });
      formGroup.get('lapDistancePassed')?.setValidators([Validators.max(raceSettings.lapLength!)])
      this.showLapDistancePassedColumn = true;
    }
    else {
      formGroup.get('lapDistancePassed')?.setValue(null, {onlySelf: true, emitEvent: false});
      formGroup.get('lapDistancePassed')?.disable({ emitEvent: false });
      this.showLapDistancePassedColumn = this.competitorControlPointTimeItems.controls.some(c => !!c.get('lapDistancePassed')?.enabled);
    }
    this.recomputeVisibleColumnCount();
    this.recomputeSaveToServerDisabled();
  }

  handleMaxLapCountControl(formGroup: FormGroup, raceId: number | undefined, index: number) {
    const control = formGroup.get('status');
    if (!control) return;
    const raceSettings: RaceSettings | undefined = raceId ? this.raceSettingsMap.get(raceId!) : undefined;
    const hasStartNumber = !!formGroup.get('startNumber')?.value;
    const hasMaxLapsCount = raceSettings?.resultProcessingType === ResultProcessingType.ROUND_RACE && !!raceSettings.maxLapsCount;
    const lap = this.measurementTimeItemHolders.at(index)?.lap ?? 0;
    const enableAndSetFinish = () => {
      control.enable({ emitEvent: false });
      if (!control.value) {
        control.setValue('FIN', { onlySelf: true, emitEvent: false });
      }
    };
    if (hasMaxLapsCount && hasStartNumber) {
      if (lap === raceSettings.maxLapsCount) {
        enableAndSetFinish();
        this.showStatusColumn = true;
        this.recomputeVisibleColumnCount();
        this.recomputeSaveToServerDisabled();
        return;
      }
      control.reset({ value: null, disabled: true }, { emitEvent: false });
      this.recomputeVisibleColumnCount();
      this.recomputeSaveToServerDisabled();
      return;
    }
    if (this.measurementsPointsView) {
      enableAndSetFinish();
      this.recomputeVisibleColumnCount();
      this.recomputeSaveToServerDisabled();
      return;
    }
    control.reset({ value: null, disabled: true }, { emitEvent: false });
    this.recomputeVisibleColumnCount();
    this.recomputeSaveToServerDisabled();
  }

  /**
   * Determines whether this row's competitor is on a concurrent team race, and if so, populates
   * the team-member select's items/labels for that row. Concurrent team races require picking
   * which team member a measurement belongs to (see sttm_race_time_checkpoint_members), since
   * ranking is done per-member and rolled up to the team by the last member's time.
   * Stores isConcurrentRow / teamMemberIds / teamMemberLabels directly on
   * measurementTimeItemHolders[index] so the template reads plain holder fields instead of
   * calling back into the component.
   */
  handleTeamMemberControl(formGroup: FormGroup, index: number, competitor?: Competitor) {
    const control = formGroup.get('teamMemberId');
    if (!control) return;
    const holder = this.measurementTimeItemHolders[index];
    if (!holder) return;

    const raceSettings = competitor?.raceId ? this.raceSettingsMap.get(competitor.raceId) : undefined;
    const isConcurrent = RaceSettings.isConcurrentRace(raceSettings) && !!competitor?.teamMembers?.length;

    holder.isConcurrentRow = isConcurrent;
    holder.teamMembers = isConcurrent ? (competitor?.teamMembers ?? []): []
    holder.teamMemberIds = isConcurrent
        ? (holder!.teamMembers ?? []).map(m => m.id!).filter(id => id != null)
        : [];
    holder.teamMemberLabels = isConcurrent
        ? (holder!.teamMembers ?? []).map(m => `${m.firstName ?? ''} ${m.lastName ?? ''}${m.startNumberExtension ? ' (' + m.startNumberExtension + ')' : ''}`.trim())
        : [];

    if (isConcurrent) {
      control.enable({ emitEvent: false });
      control.setValidators([Validators.required]);
      this.showTeamMemberColumn = true;
      if (!control.value || !holder.teamMemberIds.includes(control.value)) {
        const firstMemberId = holder.teamMemberIds[0];
        if (firstMemberId != null) {
          control.setValue(firstMemberId, { onlySelf: true, emitEvent: false });
        }
      }
    }
    else {
      control.setValue(null, { onlySelf: true, emitEvent: false });
      control.clearValidators();
      control.disable({ emitEvent: false });
      this.showTeamMemberColumn = this.competitorControlPointTimeItems.controls.some(c => !!c.get('teamMemberId')?.enabled);
    }
    control.updateValueAndValidity({ emitEvent: false });
    this.recomputeVisibleColumnCount();
    this.updateRowStartTime(index, competitor);
    this.recomputeSaveToServerDisabled();
  }

  /**
   * For concurrent rows, "has this row started" means "has the currently selected team member
   * started" — the parent/team competitor itself has no meaningful startTime in that case, only
   * its members do. For non-concurrent rows it's just the competitor's own startTime. Stored on
   * measurementTimeItemHolders[index].hasStartTime, recomputed whenever the competitor is
   * (re)fetched (via handleTeamMemberControl) and whenever the row's teamMemberId select changes
   * (via the valueChanges subscription in addMeasurementTimeItem).
   */
  private updateRowStartTime(index: number, competitor?: Competitor): void {
    const holder = this.measurementTimeItemHolders[index];
    if (!holder) return;

    if (holder.isConcurrentRow) {
      const selectedMemberId = this.competitorControlPointTimeItems.at(index)?.get('teamMemberId')?.value;
      const selectedMember = holder?.teamMembers?.find(m => m.id === selectedMemberId);
      holder.hasStartTime = !!selectedMember?.startTime;
    }
    else {
      holder.hasStartTime = !!competitor?.startTime;
    }
  }

  findChipNumbersByStatus(
      results: CompetitorSaveControlPointResult[],
      status: CompetitorSaveControlPointResultStatus
  ): string[] {
    return results
        .filter(result => result.status === status)
        .map(result => result.competitor?.chipNumber)
        .filter(chipNumber => chipNumber !== undefined) as string[];
  }

  /** Recomputes visibleColumnCount, matching the same @if flags used in the header, so the
   *  loading-row colspan stays accurate as columns turn on/off. Call after any change to
   *  showStatusColumn / showLapDistancePassedColumn / showTeamMemberColumn. */
  private recomputeVisibleColumnCount(): void {
    let count = 4; // time, startNumber, competitor, more
    if (this.measurementsPointsView) count += 3; // waypoint, penalty, checkpoints
    if (this.eventItem?.commonStartNumber ?? true) count += 1; // race
    if (!this.measurementsPointsView) count += 2; // lap, lapDistancePassed
    if (this.showStatusColumn || this.measurementsPointsView) count += 1; // status
    if (this.showTeamMemberColumn) count += 1; // team member
    this.visibleColumnCount = count;
  }

  protected readonly WaypointType = WaypointType;
  protected readonly CompetitorSaveControlPointResultStatus = CompetitorSaveControlPointResultStatus;
  private readonly ALL = -1;
}
